import { DialogTitle } from '@headlessui/react';
import { ArrowLeft2, Forbidden, Message, ProfileAdd } from 'iconsax-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedSearchParams, useTypedState } from 'react-router-typesafe-routes';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import Modal, { type ModalAction } from '@/components/common/modal/modal';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useApplyFriend from '@/hooks/contacts/use-apply-friend';
import useUserInfoByCode from '@/hooks/scan-code/use-user-info-by-code';
import useCreateSession from '@/hooks/sessions/use-create-session';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { useBlacklisted } from '@/hooks/users/use-blacklist';
import useFriendInfo from '@/hooks/users/use-friend-info';
import useUserInfo from '@/hooks/users/use-user-info';
import type { UserInfoByCode } from '@/models/user/other-user-info';
import ROUTES from '@/routes';
import { useUserId } from '@/stores/auth';
import { getSafeMediaUrl } from '@/utils/media-url';

const MAX_LENGTH = 30;

export default function ContactRequestUserRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [{ id: requestingUserId, code, key, groupId, sessionId, source }] = useTypedSearchParams(
    ROUTES.CONTACT.REQUEST.USER,
  );
  const { from: navigatedFrom } = useTypedState(ROUTES.CONTACT.REQUEST.USER);
  const remarkModalRef = useRef<ModalAction>(null);
  const [remark, setRemark] = useState('');
  const { dispatch } = useCommDispatcher();
  const currentUserId = useUserId();

  const { data: userInfoFromCode, isLoading: isUserInfoLoading } = useUserInfoByCode(code);
  const { data: userInfo } = useUserInfo();
  const { friendInfo, isLoading: isFriendInfoLoading } = useFriendInfo(requestingUserId || userInfoFromCode?.Data?.UID);
  const {
    applyFriend,
    submittedAt,
    isPending: isApplyFriendPending,
    canAddFriend,
  } = useApplyFriend({
    userId: requestingUserId || userInfoFromCode?.Data?.UID,
    qrCode: code,
    vChatId: key,
    groupId: groupId,
    sessionId: sessionId,
    source: source,
  });
  const {
    sessionId: friendSessionId,
    mutateAsync: createSession,
    isPending: isCreatingSession,
  } = useCreateSession(requestingUserId || userInfoFromCode?.Data?.UID);
  const { data: isBlacklisted } = useBlacklisted(requestingUserId || userInfoFromCode?.Data?.UID);

  const info: UserInfoByCode | null = useMemo(() => {
    if (userInfoFromCode?.Data) {
      return userInfoFromCode.Data;
    }
    if (friendInfo?.FriendInfo) {
      const friend = friendInfo.FriendInfo;
      return {
        UID: friend.FriendID ?? 0,
        NickName: friend.RemarkName || friend.FriendNickName || '',
        HeadImg: friend.FriendHeadImg || '',
        Gender: friend.FriendGender || 0,
        Sign: friend.FriendSign || '',
      } satisfies UserInfoByCode;
    }

    return null;
  }, [userInfoFromCode?.Data, friendInfo?.FriendInfo]);

  const handleApplyFriend = async () => {
    const result = await applyFriend(remark);

    remarkModalRef.current?.close();

    if (result.Code === 0 && navigatedFrom === 'comm' && friendInfo?.FriendInfo?.FriendID) {
      dispatch({ event: 'dismiss' });

      dispatch({
        event: 'pending-application',
        data: {
          type: 'session',
          id: friendInfo.FriendInfo.FriendID,
          name: friendInfo.FriendInfo.FriendNickName ?? '',
        },
      });
    }
  };

  const handleGoToSession = async () => {
    if (friendSessionId) {
      return navigate(
        ROUTES.MESSAGES.SESSIONS.$buildPath({
          params: { id: friendSessionId },
        }),
      );
    }
    const result = await createSession(info?.UID ?? 0);

    if (result.Data?.ID) {
      return navigate(
        ROUTES.MESSAGES.SESSIONS.$buildPath({
          params: {
            id: result.Data?.ID,
          },
        }),
      );
    }
  };

  const isLoading = isFriendInfoLoading || isUserInfoLoading;

  return (
    <PageLayout
      title={isLoading ? t('user.title') : info?.NickName}
      bannerColor='dark'>
      {isDesktop ? (
        <div className='font-bold text-2xl mt-4 ml-4'>{t('conversation.list.menu.add-friend')}</div>
      ) : (
        <button
          type='button'
          className='btn btn-square btn-ghost absolute left-4 top-safe-offset-4 z-20 hover:shadow'
          onClick={() => navigate(-1)}>
          <ArrowLeft2
            size={24}
            variant='Outline'
            className='aspect-square size-6 text-slate-200'
          />
        </button>
      )}

      <LoadingLayout promise={isUserInfoLoading}>
        {() => (
          <div
            className='flex size-full flex-1 flex-col items-stretch gap-4 bg-cover bg-center bg-no-repeat'
            style={{ backgroundImage: `url("${getSafeMediaUrl(info?.HeadImg)}")` }}>
            <div className='flex flex-1 flex-col items-stretch bg-neutral-950/50 px-5 text-neutral-50 desktop:bg-white desktop:text-body backdrop-blur-sm pb-safe-offset-4'>
              <div className='flex flex-1 flex-col items-center justify-center'>
                <AvatarWithStatus
                  className='size-32'
                  size={[128, 128]}
                  imageUrl={info?.HeadImg}
                  label={info?.NickName}
                />

                <h2 className='mt-6  text-h2 desktop:text-sm'>{info?.NickName ?? ''}</h2>

                {info?.Sign && <p className='mt-2 max-w-60 text-sm'>{info?.Sign}</p>}
              </div>

              {Boolean(source && info?.UID !== currentUserId) && (
                <>
                  {isBlacklisted && (
                    <button
                      type='button'
                      disabled
                      className='btn btn-primary'>
                      <Forbidden className='size-5' />

                      {t('user.blocked')}
                    </button>
                  )}
                  {!isBlacklisted && (
                    <>
                      {friendInfo?.IsFriend && (
                        <ProgressButton
                          as='button'
                          className='btn btn-primary'
                          isLoading={isCreatingSession}
                          icon={<Message className='size-5' />}
                          label='nav.messages'
                          onClick={handleGoToSession}
                        />
                      )}
                      {!friendInfo?.IsFriend && canAddFriend && (
                        <ProgressButton
                          as='button'
                          isLoading={isApplyFriendPending}
                          className='btn btn-primary'
                          icon={<ProfileAdd className='size-5' />}
                          label='user.add-friend'
                          onClick={() => remarkModalRef.current?.open()}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </LoadingLayout>

      <Modal ref={remarkModalRef}>
        <DialogTitle className='my-4 text-lg font-bold'>{t('user.add-friend-request')}</DialogTitle>

        <textarea
          name='remark'
          cols={3}
          maxLength={MAX_LENGTH}
          placeholder={t('user.remark.placeholder', { name: userInfo?.NickName })}
          className='textarea textarea-bordered w-full'
          onChange={(e) => setRemark(e.target.value)}
        />
        <div className='text-body2 text-right text-secondary'>
          {remark.length}/{MAX_LENGTH}
        </div>
        <div className='my-4 flex xs:items-center items-end justify-end gap-2'>
          <button
            type='button'
            disabled={isLoading}
            className='btn btn-ghost'
            onClick={() => {
              remarkModalRef.current?.close();
            }}>
            {t('common.cancel')}
          </button>

          <button
            type='button'
            disabled={isLoading || isApplyFriendPending}
            className='btn btn-primary'
            onClick={handleApplyFriend}>
            {isLoading || isApplyFriendPending ? (
              <span className='loading loading-spinner loading-sm' />
            ) : submittedAt ? (
              t('common.submit-again')
            ) : (
              t('common.submit')
            )}
          </button>
        </div>
      </Modal>
    </PageLayout>
  );
}
